import { safeParseDate } from "./date";
import { formatYmdLocal } from "./formatYmdLocal";
import { formatYmdInTimeZone, getRuntimeTenantTimeZone } from "./studio-clock";

export function isUtcMidnight(date: Date): boolean {
  return (
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0
  );
}

/**
 * Parses appointment API datetimes. Date-only strings (YYYY-MM-DD) are treated as
 * local calendar dates, not UTC midnight.
 */
export function parseAppointmentApiDateTime(
  value: Date | string | null | undefined,
): Date | null {
  if (value == null) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const trimmed = String(value).trim();
  if (!trimmed) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [y, m, d] = trimmed.split("-").map(Number);
    return new Date(y, m - 1, d, 12, 0, 0);
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Calendar date for grouping/display in the studio time zone.
 * A date-only string stays that civil day. Instants use the tenant zone,
 * falling back to the browser zone when the tenant zone is unknown.
 */
export function getAppointmentCalendarDate(
  value: Date | string | null | undefined,
): Date | null {
  const key = getAppointmentScheduleSlotDateKey(value);
  if (!key) return null;

  const [year, month, day] = key.split("-").map(Number);
  return new Date(year, month - 1, day, 12, 0, 0);
}

/**
 * Studio-day key for a schedule slot or booking instant.
 * Date-only strings (YYYY-MM-DD) are returned unchanged.
 */
export function getAppointmentScheduleSlotDateKey(
  value: Date | string | null | undefined,
): string | null {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return value.trim();
  }

  const parsed = parseAppointmentApiDateTime(value) ?? safeParseDate(value);
  if (!parsed) return null;

  return formatYmdInTimeZone(parsed, getRuntimeTenantTimeZone());
}

/**
 * Date key for a calendar grid cell (the labeled day the user clicks).
 * Uses civil year/month/day - never toISOString().
 */
export function getAppointmentCalendarCellDateKey(date: Date): string {
  return formatYmdLocal(date);
}

/** @deprecated Prefer getAppointmentScheduleSlotDateKey for schedule slots. */
export function getAppointmentLocalDateKey(
  value: Date | string | null | undefined,
): string | null {
  return getAppointmentScheduleSlotDateKey(value);
}

export function isSameAppointmentScheduleDay(
  cellDate: Date,
  slotValue: Date | string | null | undefined,
): boolean {
  const cellKey = getAppointmentCalendarCellDateKey(cellDate);
  const slotKey = getAppointmentScheduleSlotDateKey(slotValue);
  return slotKey != null && cellKey === slotKey;
}

export function isSameAppointmentLocalDay(
  a: Date | string | null | undefined,
  b: Date | string | null | undefined,
): boolean {
  const keyA = getAppointmentScheduleSlotDateKey(a);
  const keyB = getAppointmentScheduleSlotDateKey(b);
  return keyA != null && keyB != null && keyA === keyB;
}
